import { api } from '@/api'
import type { BlogCategory, BlogPost, BlogPostFilters } from '@/types/blog'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface CachedPosts {
  data: BlogPost[]
  filters: BlogPostFilters
  timestamp: number
  expiresAt: number
}

interface CachedCategories {
  data: BlogCategory[]
  timestamp: number
  expiresAt: number
}

const POSTS_CACHE_PREFIX = 'bdc-blog-posts-'
const CATEGORIES_CACHE_KEY = 'bdc-blog-categories'
const PUBLIC_POSTS_TTL = 15 * 60 * 1000 // 15 minutes pour posts publics
const ADMIN_POSTS_TTL = 5 * 60 * 1000 // 5 minutes pour posts admin
const CATEGORIES_TTL = 30 * 60 * 1000 // 30 minutes pour catégories

export const useBlogStore = defineStore('blog', () => {
  // État réactif
  const publicPosts = ref<BlogPost[]>([])
  const adminPosts = ref<BlogPost[]>([])
  const categories = ref<BlogCategory[]>([])
  const isLoading = ref(false)
  const isLoadingCategories = ref(false)
  const error = ref<string | null>(null)
  const lastFetchTime = ref<Map<string, number>>(new Map())

  // Getters computed
  const postsCount = computed(() => publicPosts.value.length)
  const categoriesCount = computed(() => categories.value.length)

  /**
   * Générer une clé de cache pour les posts basée sur les filtres
   */
  function getPostsCacheKey(filters?: BlogPostFilters): string {
    const key = filters
      ? JSON.stringify({
          isDraft: filters.isDraft,
          categoryId: filters.categoryId,
          authorId: filters.authorId,
        })
      : 'default'
    return `${POSTS_CACHE_PREFIX}${key}`
  }

  /**
   * Charger les posts depuis le cache localStorage
   */
  function loadPostsFromCache(filters?: BlogPostFilters): BlogPost[] | null {
    try {
      const cacheKey = getPostsCacheKey(filters)
      const cached = localStorage.getItem(cacheKey)
      if (!cached) return null

      const cachedData: CachedPosts = JSON.parse(cached)
      const now = Date.now()

      // Déterminer le TTL selon si c'est pour admin ou public
      const isAdmin = filters?.isDraft === true
      const ttl = isAdmin ? ADMIN_POSTS_TTL : PUBLIC_POSTS_TTL

      if (now < cachedData.timestamp + ttl) {
        //console.log('[BdC-BlogStore] Posts chargés depuis le cache')
        return cachedData.data
      }

      localStorage.removeItem(cacheKey)
      return null
    } catch (err) {
      console.warn('[BdC-BlogStore] Erreur lecture cache posts:', err)
      return null
    }
  }

  /**
   * Sauvegarder les posts dans le cache localStorage
   */
  function savePostsToCache(posts: BlogPost[], filters?: BlogPostFilters): void {
    try {
      const cacheKey = getPostsCacheKey(filters)
      const cached: CachedPosts = {
        data: posts,
        filters: filters || {},
        timestamp: Date.now(),
        expiresAt: Date.now() + (filters?.isDraft === true ? ADMIN_POSTS_TTL : PUBLIC_POSTS_TTL),
      }
      localStorage.setItem(cacheKey, JSON.stringify(cached))
      //console.log('[BdC-BlogStore] Posts sauvegardés dans le cache')
    } catch (err) {
      console.warn('[BdC-BlogStore] Erreur sauvegarde cache posts:', err)
    }
  }

  /**
   * Charger les catégories depuis le cache localStorage
   */
  function loadCategoriesFromCache(): BlogCategory[] | null {
    try {
      const cached = localStorage.getItem(CATEGORIES_CACHE_KEY)
      if (!cached) return null

      const cachedData: CachedCategories = JSON.parse(cached)
      const now = Date.now()

      if (now < cachedData.expiresAt) {
        //console.log('[BdC-BlogStore] Catégories chargées depuis le cache')
        return cachedData.data
      }

      localStorage.removeItem(CATEGORIES_CACHE_KEY)
      return null
    } catch (err) {
      console.warn('[BdC-BlogStore] Erreur lecture cache catégories:', err)
      localStorage.removeItem(CATEGORIES_CACHE_KEY)
      return null
    }
  }

  /**
   * Sauvegarder les catégories dans le cache localStorage
   */
  function saveCategoriesToCache(data: BlogCategory[]): void {
    try {
      const cached: CachedCategories = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + CATEGORIES_TTL,
      }
      localStorage.setItem(CATEGORIES_CACHE_KEY, JSON.stringify(cached))
      //console.log('[BdC-BlogStore] Catégories sauvegardées dans le cache')
    } catch (err) {
      console.warn('[BdC-BlogStore] Erreur sauvegarde cache catégories:', err)
    }
  }

  /**
   * Charger les posts avec filtres
   * @param filters Filtres de recherche
   * @param forceRefresh Force le rechargement depuis l'API même si le cache est valide
   */
  async function loadPosts(filters?: BlogPostFilters, forceRefresh = false): Promise<BlogPost[]> {
    // Vérifier le cache d'abord si pas de force refresh
    if (!forceRefresh) {
      const cached = loadPostsFromCache(filters)
      if (cached && cached.length >= 0) {
        // isDraft: true → adminPosts (brouillons), isDraft: false ou undefined → publicPosts (publiés)
        const isAdmin = filters?.isDraft === true
        if (isAdmin) {
          adminPosts.value = cached
        } else {
          publicPosts.value = cached
        }
        const cacheKey = getPostsCacheKey(filters)
        lastFetchTime.value.set(cacheKey, Date.now())
        //console.log(`[BdC-BlogStore] ${cached.length} posts chargés depuis le cache`)
        return cached
      }
    }

    isLoading.value = true
    error.value = null
    try {
      //console.log("[BdC-BlogStore] Chargement posts depuis l'API:", filters)
      const response = await api.blog.getPosts(filters)
      const posts = response.data

      // isDraft: true → adminPosts (brouillons), isDraft: false ou undefined → publicPosts (publiés)
      const isAdmin = filters?.isDraft === true
      if (isAdmin) {
        adminPosts.value = posts
      } else {
        publicPosts.value = posts
      }

      const cacheKey = getPostsCacheKey(filters)
      lastFetchTime.value.set(cacheKey, Date.now())
      savePostsToCache(posts, filters)
      //console.log(`[BdC-BlogStore] ${posts.length} posts chargés depuis l'API`)
      return posts
    } catch (err) {
      console.error('[BdC-BlogStore] Erreur chargement posts:', err)
      error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des posts'
      return []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Charger les catégories
   * @param forceRefresh Force le rechargement depuis l'API même si le cache est valide
   */
  async function loadCategories(forceRefresh = false): Promise<BlogCategory[]> {
    // Vérifier le cache d'abord si pas de force refresh
    if (!forceRefresh) {
      const cached = loadCategoriesFromCache()
      if (cached && cached.length >= 0) {
        categories.value = cached
        lastFetchTime.value.set(CATEGORIES_CACHE_KEY, Date.now())
        //console.log(`[BdC-BlogStore] ${cached.length} catégories chargées depuis le cache`)
        return cached
      }
    }

    isLoadingCategories.value = true
    try {
      //console.log("[BdC-BlogStore] Chargement catégories depuis l'API")
      const response = await api.blog.getCategories()
      categories.value = response.data
      lastFetchTime.value.set(CATEGORIES_CACHE_KEY, Date.now())
      saveCategoriesToCache(response.data)
      //console.log(`[BdC-BlogStore] ${response.data.length} catégories chargées depuis l'API`)
      return response.data
    } catch (err) {
      console.error('[BdC-BlogStore] Erreur chargement catégories:', err)
      return []
    } finally {
      isLoadingCategories.value = false
    }
  }

  /**
   * Invalider le cache des posts
   */
  function invalidatePostsCache(filters?: BlogPostFilters): void {
    if (filters) {
      const cacheKey = getPostsCacheKey(filters)
      localStorage.removeItem(cacheKey)
      //console.log(`[BdC-BlogStore] Cache posts invalidé pour filtres:`, filters)
    } else {
      // Invalider tous les caches de posts
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(POSTS_CACHE_PREFIX)) {
          localStorage.removeItem(key)
        }
      }
      //console.log('[BdC-BlogStore] Tous les caches posts invalidés')
    }
  }

  /**
   * Invalider le cache des catégories
   */
  function invalidateCategoriesCache(): void {
    localStorage.removeItem(CATEGORIES_CACHE_KEY)
    //console.log('[BdC-BlogStore] Cache catégories invalidé')
  }

  /**
   * Invalider tous les caches
   */
  function invalidateAllCaches(): void {
    invalidatePostsCache()
    invalidateCategoriesCache()
    publicPosts.value = []
    adminPosts.value = []
    categories.value = []
    lastFetchTime.value.clear()
    //console.log('[BdC-BlogStore] Tous les caches invalidés')
  }

  return {
    // État
    publicPosts,
    adminPosts,
    categories,
    isLoading,
    isLoadingCategories,
    error,
    lastFetchTime,

    // Getters
    postsCount,
    categoriesCount,

    // Actions
    loadPosts,
    loadCategories,
    invalidatePostsCache,
    invalidateCategoriesCache,
    invalidateAllCaches,
  }
})
